import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Save, Pencil, X } from "lucide-react";
import api from "./api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const CATEGORIES = ["tubers", "boxes", "workshops", "apparel"];

const blankProduct = (defaultCategory = "tubers") => ({
  slug: "",
  category: defaultCategory,
  name: "",
  blurb: "",
  description: "",
  image_url: "",
  base_price: 0,
  active: true,
  variants: [],
});

const blankVariant = () => ({ label: "", sku: "", price_delta: 0, stock: 0 });

export default function ConsoleProducts({ kind = "all" }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product object or null
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/products");
      setProducts(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
    }
  };

  useEffect(() => { load(); }, []);

  // Filter by kind when in tubers/boxes mode
  const visibleProducts = (() => {
    if (kind === "tubers") return products.filter((p) => p.category === "tubers");
    if (kind === "boxes") return products.filter((p) => p.category === "boxes");
    return products;
  })();

  const newProductDefault = () => {
    if (kind === "tubers") return blankProduct("tubers");
    if (kind === "boxes") return blankProduct("boxes");
    return blankProduct();
  };

  const heading = kind === "tubers" ? "Tu" : kind === "boxes" ? "Bo" : "Pro";
  const headingTail = kind === "tubers" ? "bers." : kind === "boxes" ? "xes." : "ducts.";
  const kicker = kind === "tubers" ? "✦ TUBER INVENTORY ✦" : kind === "boxes" ? "✦ BOX PRODUCTS ✦" : "✦ INVENTORY ✦";

  const save = async () => {
    if (!editing.name || !editing.slug || editing.base_price < 0) {
      toast.error("Slug, name and price are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...editing, base_price: Number(editing.base_price), variants: editing.variants.map((v) => ({ ...v, price_delta: Number(v.price_delta || 0), stock: Number(v.stock || 0) })) };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post(`/admin/products`, payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || "").join(" · ") : "Couldn't save";
      toast.error(msg || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
            {kicker}
          </span>
          <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-2 leading-[1.05]">
            {heading}<span className="font-display-italic text-[var(--dahlias-sage-deep)]">{headingTail}</span>
          </h1>
        </div>
        <Button
          onClick={() => setEditing(newProductDefault())}
          data-testid="admin-product-new"
          className="h-12 px-6 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md"
        >
          <Plus className="w-4 h-4 mr-1" /> {kind === "boxes" ? "New box" : kind === "tubers" ? "New tuber" : "New product"}
        </Button>
      </div>

      <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 overflow-x-auto wobble-a">
        <table className="w-full text-base" data-testid="admin-products-table">
          <thead>
            <tr className="text-left text-xs font-label tracking-[0.35em] uppercase text-[var(--dahlias-sage-deep)] border-b border-[var(--dahlias-sage)]/20">
              <th className="p-4">Product</th>
              <th className="p-4">Category</th>
              <th className="p-4">Price</th>
              <th className="p-4">Stock</th>
              <th className="p-4">Active</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {visibleProducts.map((p) => {
              const stock = (p.variants || []).reduce((a, v) => a + (v.stock || 0), 0);
              return (
                <tr key={p.id} className="border-b border-[var(--dahlias-ink)]/5">
                  <td className="p-4">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[var(--dahlias-ink)]/60">{p.slug}</div>
                  </td>
                  <td className="p-4 capitalize">{p.category}</td>
                  <td className="p-4">NZ${p.base_price.toFixed(2)}</td>
                  <td className="p-4">{stock}{(p.variants || []).length ? ` (${p.variants.length} var)` : ""}</td>
                  <td className="p-4">{p.active ? "Yes" : "No"}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEditing(p)} className="p-2 hover:bg-[var(--dahlias-cream)] rounded" data-testid={`admin-product-edit-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p.id)} className="p-2 hover:bg-[var(--dahlias-cream)] rounded text-[var(--dahlias-rust)]" data-testid={`admin-product-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[var(--dahlias-ink)]/60">No products yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-product-modal">
          <div className="bg-[var(--dahlias-cream)] w-full max-w-3xl wobble-a p-6 md:p-8 max-h-[90vh] overflow-y-auto border-2 border-[var(--dahlias-sage)]/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">
                {editing.id ? "Edit product" : "New product"}
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-[var(--dahlias-paper)] rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Slug (URL)" testid="prod-slug">
                <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} required />
              </Field>
              <Field label="Category">
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="prod-category"
                  className="h-12 w-full text-base bg-[var(--dahlias-paper)] border border-[var(--dahlias-ink)]/15 rounded-sm px-3"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Name" testid="prod-name" full>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
              </Field>
              <Field label="Blurb" testid="prod-blurb" full>
                <Input value={editing.blurb} onChange={(e) => setEditing({ ...editing, blurb: e.target.value })} />
              </Field>
              <Field label="Description" testid="prod-desc" full>
                <Textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </Field>
              <Field label="Image URL" testid="prod-img" full>
                <Input value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="/photos/your-image.jpg" />
              </Field>
              <Field label="Base price (NZ$)" testid="prod-price">
                <Input type="number" step="0.01" min="0" value={editing.base_price} onChange={(e) => setEditing({ ...editing, base_price: e.target.value })} />
              </Field>
              <Field label="Active">
                <label className="flex items-center gap-2 h-12">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-5 h-5 accent-[var(--dahlias-forest)]" data-testid="prod-active" />
                  <span>Visible in shop</span>
                </label>
              </Field>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <h3 className="font-display text-xl text-[var(--dahlias-forest)]">Variants</h3>
              <Button
                type="button"
                onClick={() => setEditing({ ...editing, variants: [...editing.variants, blankVariant()] })}
                data-testid="prod-variant-add"
                variant="outline"
                className="h-10"
              >
                <Plus className="w-4 h-4 mr-1" /> Add variant
              </Button>
            </div>

            <div className="mt-3 space-y-3" data-testid="prod-variants">
              {editing.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 bg-[var(--dahlias-paper)] p-3 rounded-md">
                  <Input className="col-span-12 md:col-span-4" placeholder="Label (e.g. Size M)" value={v.label} onChange={(e) => {
                    const nv = [...editing.variants]; nv[i] = { ...v, label: e.target.value }; setEditing({ ...editing, variants: nv });
                  }} data-testid={`prod-variant-${i}-label`} />
                  <Input className="col-span-6 md:col-span-3" placeholder="SKU" value={v.sku || ""} onChange={(e) => {
                    const nv = [...editing.variants]; nv[i] = { ...v, sku: e.target.value }; setEditing({ ...editing, variants: nv });
                  }} data-testid={`prod-variant-${i}-sku`} />
                  <Input className="col-span-6 md:col-span-2" type="number" step="0.01" placeholder="Δ price" value={v.price_delta} onChange={(e) => {
                    const nv = [...editing.variants]; nv[i] = { ...v, price_delta: e.target.value }; setEditing({ ...editing, variants: nv });
                  }} data-testid={`prod-variant-${i}-delta`} />
                  <Input className="col-span-6 md:col-span-2" type="number" min="0" placeholder="Stock" value={v.stock} onChange={(e) => {
                    const nv = [...editing.variants]; nv[i] = { ...v, stock: e.target.value }; setEditing({ ...editing, variants: nv });
                  }} data-testid={`prod-variant-${i}-stock`} />
                  <button type="button" onClick={() => {
                    const nv = editing.variants.filter((_, j) => j !== i); setEditing({ ...editing, variants: nv });
                  }} className="col-span-6 md:col-span-1 flex items-center justify-center text-[var(--dahlias-rust)] hover:bg-[var(--dahlias-cream)] rounded" data-testid={`prod-variant-${i}-remove`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {editing.variants.length === 0 && (
                <p className="text-sm text-[var(--dahlias-ink)]/60">No variants. Single-SKU product. Add a variant if you have sizes / colours / dates.</p>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="button" disabled={busy} onClick={save} data-testid="prod-save" className="bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill px-7">
                <Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save product"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <Label className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
